import {
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  lazy,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Auth } from 'api';

import useConfirmSignUp from 'services/auth/useConfirmSignUp';
import { getEmail, storeEmail } from 'store/user';
import { useAppDispatch } from 'store/store';

import { ButtonSizes, ButtonVariants, DesignButton } from 'components';

import styles from '../auth.module.scss';
const Spinfinity = lazy(() =>
  import('@repo/ui').then(module => ({ default: module.Spinfinity })),
);
const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userEmail = useSelector(getEmail);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const encodedEmail = searchParams.get('user');
  const emailURL = encodedEmail ? window.atob(encodedEmail) : '';
  const [error, setError] = useState('');

  const confirmSignUp = useConfirmSignUp();
  const hasSubmittedRef = useRef(false);
  const onSubmitConfirmation = useCallback(
    async (verificationCode: string) => {
      try {
        await confirmSignUp(verificationCode);
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === 'CodeMismatchException') {
            setError('Invalid verification code.');
          } else if (error.name === 'ExpiredCodeException') {
            setError('Verification code expired.');
          }
        } else {
          setError('GenericError');
        }
      }
    },
    [confirmSignUp],
  );

  useEffect(() => {
    const handleLanding = async () => {
      if (!token || !encodedEmail) {
        return navigate('reset-password');
      }
      if (!userEmail || userEmail !== emailURL) {
        await dispatch(storeEmail(emailURL));
      }
      if (token && userEmail === emailURL && !hasSubmittedRef.current) {
        hasSubmittedRef.current = true;
        await onSubmitConfirmation(token);
      }
    };
    handleLanding();
  }, [
    userEmail,
    emailURL,
    navigate,
    token,
    encodedEmail,
    dispatch,
    onSubmitConfirmation,
  ]);

  const reSendCode = async (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!userEmail) {
      navigate('/reset-password');
      toast.error('Something went wrong, please try again');
    } else {
      await Auth.resendSignUp(userEmail);
      toast.success('Email sent! Please check your inbox');
      navigate('/check-email');
    }
  };

  if (error)
    return (
      <div className={styles.authForm}>
        <h2 className='mb-3'>Verification failed</h2>
        <p className={styles.secondaryText}>
          We were not able verify your email {userEmail}. Please try again.
        </p>
        <p className={styles.subtext}>
          Reach out hello@tinyhealth.com if you continue to have issues.
        </p>
        {error !== 'GenericError' && <div className='mb-2'>{error}</div>}
        <div onClick={reSendCode}>
          <DesignButton
            label='Resend verification email'
            variant={ButtonVariants.TEXT}
            size={ButtonSizes.M}
            width={'full'}
          />
        </div>
      </div>
    );

  return (
    <div className='w-100'>
      <div className={styles.authForm}>
        <Spinfinity />
        <p className={styles.subtext}>Verifying email...</p>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
